import Tesseract from 'tesseract.js'

const preprocessAddressImage = (canvas: HTMLCanvasElement): HTMLCanvasElement => {
  const ctx = canvas.getContext('2d')
  if (!ctx) return canvas

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const data = imageData.data

  // Enhanced preprocessing for address text
  // Addresses can have varying contrast in real photos
  for (let i = 0; i < data.length; i += 4) {
    // Convert to grayscale
    const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]

    // Apply adaptive threshold with higher contrast
    const threshold = 140 // Slightly higher threshold for cleaner text
    let value: number

    if (gray < threshold) {
      // Dark pixels (text) - make them much darker
      value = Math.max(0, gray - 40)
    } else {
      // Light pixels (background) - make them much lighter
      value = Math.min(255, gray + 40)
    }

    data[i] = value
    data[i + 1] = value
    data[i + 2] = value
  }

  ctx.putImageData(imageData, 0, 0)

  // Apply sharpening and contrast boost
  const sharpenedCanvas = document.createElement('canvas')
  sharpenedCanvas.width = canvas.width
  sharpenedCanvas.height = canvas.height
  const sharpenCtx = sharpenedCanvas.getContext('2d')

  if (sharpenCtx) {
    sharpenCtx.filter = 'contrast(1.3) brightness(1.05)'
    sharpenCtx.drawImage(canvas, 0, 0)
    return sharpenedCanvas
  }

  return canvas
}

const findTextRegions = (
  canvas: HTMLCanvasElement,
  labelBounds: {x: number; y: number; width: number; height: number} | null,
  searchArea: 'top' | 'middle'
): Array<{y: number; height: number}> => {
  const ctx = canvas.getContext('2d')
  if (!ctx) return []

  const baseX = labelBounds?.x || 0
  const baseY = labelBounds?.y || 0
  const baseWidth = labelBounds?.width || canvas.width
  const baseHeight = labelBounds?.height || canvas.height

  const imageData = ctx.getImageData(baseX, baseY, baseWidth, baseHeight)
  const data = imageData.data

  // Define search range based on area
  let startY: number, endY: number
  if (searchArea === 'top') {
    // FROM address: top 20-50% of label
    startY = Math.floor(baseHeight * 0.2)
    endY = Math.floor(baseHeight * 0.5)
  } else {
    // TO address: middle 35-70% of label
    startY = Math.floor(baseHeight * 0.35)
    endY = Math.floor(baseHeight * 0.7)
  }

  // Scan for text density (dark pixels indicating text)
  const densityMap: number[] = []
  for (let y = startY; y < endY; y++) {
    let darkPixels = 0
    for (let x = 0; x < baseWidth; x++) {
      const idx = (y * baseWidth + x) * 4
      const brightness = (data[idx] + data[idx + 1] + data[idx + 2]) / 3
      if (brightness < 180) darkPixels++ // Count darker pixels (text)
    }
    densityMap.push(darkPixels / baseWidth)
  }

  // Find regions with high text density
  const regions: Array<{y: number; height: number}> = []
  const avgDensity = densityMap.reduce((a, b) => a + b, 0) / densityMap.length
  const threshold = avgDensity * 1.5 // 50% above average

  let regionStart = -1
  for (let i = 0; i < densityMap.length; i++) {
    if (densityMap[i] > threshold && regionStart === -1) {
      regionStart = i
    } else if (densityMap[i] <= threshold && regionStart !== -1) {
      const regionHeight = i - regionStart
      if (regionHeight >= baseHeight * 0.08 && regionHeight <= baseHeight * 0.3) {
        regions.push({
          y: baseY + startY + regionStart,
          height: regionHeight,
        })
      }
      regionStart = -1
    }
  }

  // If we found regions, return them; otherwise return default regions
  if (regions.length > 0) {
    return regions.slice(0, 3) // Return up to 3 best regions
  }

  // Fallback to fixed positions
  if (searchArea === 'top') {
    return [{y: baseY + baseHeight * 0.25, height: baseHeight * 0.2}]
  } else {
    return [{y: baseY + baseHeight * 0.4, height: baseHeight * 0.25}]
  }
}

const scoreAddressText = (text: string): number => {
  if (!text || text.length < 10) return 0

  let score = 0

  // Check for common address patterns
  const hasStreetNumber = /\b\d+\b/.test(text)
  const hasStreetName =
    /\b(st|street|ave|avenue|rd|road|blvd|boulevard|dr|drive|ln|lane|way|ct|court)\b/i.test(text)
  const hasCity = /[a-z]{3,}/i.test(text)
  const hasZip = /\b\d{5}(-\d{4})?\b/.test(text)
  const hasState = /\b[A-Z]{2}\b/.test(text)

  if (hasStreetNumber) score += 20
  if (hasStreetName) score += 15
  if (hasCity) score += 15
  if (hasZip) score += 25
  if (hasState) score += 15

  // Penalize very short or very long text
  const lineCount = text.split('\n').filter((l) => l.trim().length > 0).length
  if (lineCount >= 3 && lineCount <= 5) score += 10

  // Bonus for reasonable text length
  if (text.length >= 30 && text.length <= 200) score += 10

  return score
}

export const extractAddresses = async (
  canvas: HTMLCanvasElement,
  worker: Tesseract.Worker | null
): Promise<
  | {
      success: true
      from: string
      to: string
      regions: {
        to: {x: number; y: number; width: number; height: number}
      }
    }
  | {success: false}
> => {
  const ctx = canvas.getContext('2d')
  if (!ctx || !worker) return {success: false}

  const width = canvas.width
  const height = canvas.height

  // Step 1: Detect label boundaries
  const labelBounds = detectLabelBoundaries(canvas)

  // Use label bounds for coordinate normalization
  const baseX = labelBounds?.x || 0
  const baseY = labelBounds?.y || 0
  const baseWidth = labelBounds?.width || width
  const baseHeight = labelBounds?.height || height

  // Step 2: Find text regions dynamically
  const fromRegions = findTextRegions(canvas, labelBounds, 'top')
  const toRegions = findTextRegions(canvas, labelBounds, 'middle')

  // Configure Tesseract for address text
  await worker.setParameters({
    tessedit_pageseg_mode: Tesseract.PSM.AUTO,
    tessedit_ocr_engine_mode: Tesseract.OEM.LSTM_ONLY,
  })

  // Try multiple regions for FROM address (still needed for OCR extraction)
  const fromResults: Array<{text: string; score: number; confidence: number}> = []

  for (const region of fromRegions) {
    // Try different horizontal positions (left-aligned, slightly indented)
    const xPositions = [
      {x: 0, w: 0.45}, // Left edge, 45% width
      {x: 0.05, w: 0.4}, // Slightly indented, 40% width
      {x: 0, w: 0.5}, // Left edge, 50% width
    ]

    for (const pos of xPositions) {
      const fromCanvas = document.createElement('canvas')
      const fromWidth = baseWidth * pos.w
      const fromHeight = region.height
      fromCanvas.width = fromWidth
      fromCanvas.height = fromHeight

      const fromCtx = fromCanvas.getContext('2d')
      if (!fromCtx) continue

      const sourceX = baseX + baseWidth * pos.x
      fromCtx.drawImage(
        canvas,
        sourceX,
        region.y,
        fromWidth,
        fromHeight,
        0,
        0,
        fromWidth,
        fromHeight
      )
      preprocessAddressImage(fromCanvas)

      try {
        const result = await worker.recognize(fromCanvas)
        const text = result.data.text.trim()
        const score = scoreAddressText(text)

        if (text && score > 0) {
          fromResults.push({
            text,
            score,
            confidence: result.data.confidence,
          })
        }
      } catch (error) {
        // Silent fail
      }
    }
  }

  // Try multiple regions for TO address
  const toResults: Array<{
    text: string
    score: number
    confidence: number
    regionNum: number
    x: number
    y: number
    width: number
    height: number
  }> = []

  let toRegionNum = 0
  for (const region of toRegions) {
    // Try different horizontal positions
    // Region 1 configuration (x: 0.2, w: 0.6) typically works best for real photos
    const xPositions = [
      {x: 0.2, w: 0.6}, // Region 1: More right, 60% width (BEST for real photos)
      {x: 0.15, w: 0.65}, // Region 2: Slightly left of center, 65% width
      {x: 0.1, w: 0.7}, // Region 3: More left, 70% width
      {x: 0.25, w: 0.55}, // Region 4: Even more right, 55% width
    ]

    for (const pos of xPositions) {
      toRegionNum++
      const toCanvas = document.createElement('canvas')
      const toWidth = baseWidth * pos.w
      const toHeight = region.height
      toCanvas.width = toWidth
      toCanvas.height = toHeight

      const toCtx = toCanvas.getContext('2d')
      if (!toCtx) continue

      const sourceX = baseX + baseWidth * pos.x
      const sourceY = region.y

      toCtx.drawImage(canvas, sourceX, sourceY, toWidth, toHeight, 0, 0, toWidth, toHeight)
      preprocessAddressImage(toCanvas)

      try {
        const result = await worker.recognize(toCanvas)
        const text = result.data.text.trim()
        const score = scoreAddressText(text)

        if (text && score > 0) {
          // Normalize coordinates relative to label bounds, not full canvas
          toResults.push({
            text,
            score,
            confidence: result.data.confidence,
            regionNum: toRegionNum,
            x: (sourceX - baseX) / baseWidth, // Relative to label
            y: (sourceY - baseY) / baseHeight, // Relative to label
            width: toWidth / baseWidth,
            height: toHeight / baseHeight,
          })
        }
      } catch (error) {
        // Silent fail
      }
    }
  }

  // Select best results based on score and confidence
  if (fromResults.length === 0 || toResults.length === 0) {
    return {success: false}
  }

  const bestFrom = fromResults.reduce((best, current) => {
    const currentTotal = current.score + current.confidence * 0.5
    const bestTotal = best.score + best.confidence * 0.5
    return currentTotal > bestTotal ? current : best
  })

  const bestTo = toResults.reduce((best, current) => {
    // Give bonus to region 1 (x: 0.2, w: 0.6 for real photos)
    const currentBonus = current.regionNum === 1 ? 10 : 0
    const bestBonus = best.regionNum === 1 ? 10 : 0

    const currentTotal = current.score + current.confidence * 0.5 + currentBonus
    const bestTotal = best.score + best.confidence * 0.5 + bestBonus
    return currentTotal > bestTotal ? current : best
  })

  return {
    success: true,
    from: bestFrom.text,
    to: bestTo.text,
    regions: {
      to: {
        x: bestTo.x,
        y: bestTo.y,
        width: bestTo.width,
        height: bestTo.height,
      },
    },
  }
}

const findBarcodeRegion = (
  canvas: HTMLCanvasElement,
  labelBounds: {x: number; y: number; width: number; height: number} | null
): {y: number; height: number} | null => {
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  // Use label bounds if available, otherwise use full canvas
  const baseX = labelBounds?.x || 0
  const baseY = labelBounds?.y || 0
  const baseWidth = labelBounds?.width || canvas.width
  const baseHeight = labelBounds?.height || canvas.height

  const imageData = ctx.getImageData(baseX, baseY, baseWidth, baseHeight)
  const data = imageData.data

  // Scan the bottom 50% of the label to find the barcode
  // Barcode has dense vertical black lines with many transitions
  const startY = Math.floor(baseHeight * 0.5)
  const endY = baseHeight

  let maxDensity = 0
  let barcodeY = startY
  let barcodeHeight = 0

  // Scan horizontal lines to find the densest area (barcode bars)
  const densityMap: number[] = []

  for (let y = startY; y < endY; y++) {
    let blackPixels = 0
    let transitions = 0
    let lastWasBlack = false

    for (let x = 0; x < baseWidth; x++) {
      const idx = (y * baseWidth + x) * 4
      const brightness = (data[idx] + data[idx + 1] + data[idx + 2]) / 3
      const isBlack = brightness < 128

      if (isBlack) blackPixels++

      // Count transitions (black to white, white to black)
      if (isBlack !== lastWasBlack) transitions++
      lastWasBlack = isBlack
    }

    // Barcode has high density of black pixels AND many transitions
    // Normalize by width and weight transitions heavily
    const density = (blackPixels / baseWidth) * (transitions / 20)
    densityMap.push(density)

    if (density > maxDensity) {
      maxDensity = density
      barcodeY = y
    }
  }

  // Find the start and end of the barcode region (where density is high)
  const threshold = maxDensity * 0.3 // 30% of max density
  let barcodeStart = barcodeY
  let barcodeEnd = barcodeY

  // Scan upward from peak to find start
  for (let i = barcodeY - startY; i >= 0; i--) {
    if (densityMap[i] < threshold) {
      barcodeStart = startY + i
      break
    }
  }

  // Scan downward from peak to find end
  for (let i = barcodeY - startY; i < densityMap.length; i++) {
    if (densityMap[i] < threshold) {
      barcodeEnd = startY + i
      break
    }
  }

  barcodeHeight = barcodeEnd - barcodeStart

  // Validate barcode region
  if (barcodeHeight < baseHeight * 0.05 || barcodeHeight > baseHeight * 0.4) {
    return null
  }

  return {
    y: baseY + barcodeStart,
    height: barcodeHeight,
  }
}

const detectLabelBoundaries = (
  canvas: HTMLCanvasElement
): {x: number; y: number; width: number; height: number} | null => {
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  const width = canvas.width
  const height = canvas.height
  const imageData = ctx.getImageData(0, 0, width, height)
  const data = imageData.data

  // Find the label by detecting edges (transition from background to white label)
  // Scan from edges inward to find where the label starts

  let topBoundary = 0
  let bottomBoundary = height
  let leftBoundary = 0
  let rightBoundary = width

  // Scan from top to find top edge of label
  for (let y = 0; y < height * 0.5; y++) {
    let whitePixels = 0
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4
      const brightness = (data[idx] + data[idx + 1] + data[idx + 2]) / 3
      if (brightness > 200) whitePixels++ // Count bright pixels (label is white)
    }
    // If more than 60% of the row is white, we've found the label
    if (whitePixels / width > 0.6) {
      topBoundary = Math.max(0, y - 10) // Add small margin
      break
    }
  }

  // Scan from bottom to find bottom edge of label
  for (let y = height - 1; y > height * 0.5; y--) {
    let whitePixels = 0
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4
      const brightness = (data[idx] + data[idx + 1] + data[idx + 2]) / 3
      if (brightness > 200) whitePixels++
    }
    if (whitePixels / width > 0.6) {
      bottomBoundary = Math.min(height, y + 10)
      break
    }
  }

  // Scan from left to find left edge of label
  for (let x = 0; x < width * 0.5; x++) {
    let whitePixels = 0
    for (let y = 0; y < height; y++) {
      const idx = (y * width + x) * 4
      const brightness = (data[idx] + data[idx + 1] + data[idx + 2]) / 3
      if (brightness > 200) whitePixels++
    }
    if (whitePixels / height > 0.6) {
      leftBoundary = Math.max(0, x - 10)
      break
    }
  }

  // Scan from right to find right edge of label
  for (let x = width - 1; x > width * 0.5; x--) {
    let whitePixels = 0
    for (let y = 0; y < height; y++) {
      const idx = (y * width + x) * 4
      const brightness = (data[idx] + data[idx + 1] + data[idx + 2]) / 3
      if (brightness > 200) whitePixels++
    }
    if (whitePixels / height > 0.6) {
      rightBoundary = Math.min(width, x + 10)
      break
    }
  }

  const labelWidth = rightBoundary - leftBoundary
  const labelHeight = bottomBoundary - topBoundary

  // Validate that we found a reasonable label
  if (labelWidth < width * 0.3 || labelHeight < height * 0.3) {
    return null
  }

  return {
    x: leftBoundary,
    y: topBoundary,
    width: labelWidth,
    height: labelHeight,
  }
}

const preprocessBarcodeImage = (canvas: HTMLCanvasElement): HTMLCanvasElement => {
  const ctx = canvas.getContext('2d')
  if (!ctx) return canvas

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const data = imageData.data

  // Lighter preprocessing for clear black text on white background
  // Focus on cleaning up noise while preserving text clarity
  for (let i = 0; i < data.length; i += 4) {
    // Convert to grayscale first
    const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]

    // Apply adaptive threshold - sharpen the contrast between text and background
    // This helps OCR distinguish characters better
    const threshold = 128
    let value: number

    if (gray < threshold) {
      // Dark pixels (text) - make them darker
      value = Math.max(0, gray - 30)
    } else {
      // Light pixels (background) - make them lighter
      value = Math.min(255, gray + 30)
    }

    data[i] = value
    data[i + 1] = value
    data[i + 2] = value
  }

  ctx.putImageData(imageData, 0, 0)

  // Apply slight sharpening to improve edge definition
  const sharpenedCanvas = document.createElement('canvas')
  sharpenedCanvas.width = canvas.width
  sharpenedCanvas.height = canvas.height
  const sharpenCtx = sharpenedCanvas.getContext('2d')

  if (sharpenCtx) {
    sharpenCtx.filter = 'contrast(1.2) brightness(1.1)'
    sharpenCtx.drawImage(canvas, 0, 0)
    return sharpenedCanvas
  }

  return canvas
}

const extractTrackingNumber = (text: string): string => {
  // Remove all whitespace and newlines
  const cleaned = text.replace(/\s+/g, '').replace(/[^0-9]/g, '')

  // Try to find continuous digit sequences
  const digitSequences = cleaned.match(/\d+/g) || []

  // Look for USPS tracking patterns (20-22 digits)
  for (const seq of digitSequences) {
    if (seq.length >= 20 && seq.length <= 22) {
      return seq
    }
  }

  // If no perfect match, try to find the longest digit sequence
  const longestSequence = digitSequences.reduce(
    (longest, current) => (current.length > longest.length ? current : longest),
    ''
  )

  if (longestSequence.length >= 18) {
    return longestSequence
  }

  return cleaned
}

export const extractBarcodeNumber = async (
  canvas: HTMLCanvasElement,
  worker: Tesseract.Worker | null
): Promise<
  | {
      success: true
      barcode: string
      region: {x: number; y: number; width: number; height: number}
    }
  | {success: false}
> => {
  const ctx = canvas.getContext('2d')
  if (!ctx || !worker) return {success: false}

  const width = canvas.width
  const height = canvas.height

  // Step 1: Detect label boundaries
  const labelBounds = detectLabelBoundaries(canvas)

  // Use label bounds for coordinate normalization
  const baseX = labelBounds?.x || 0
  const baseY = labelBounds?.y || 0
  const baseWidth = labelBounds?.width || width
  const baseHeight = labelBounds?.height || height

  // Step 2: Find the barcode region within the label
  const barcodeRegion = findBarcodeRegion(canvas, labelBounds)

  if (!barcodeRegion) {
    // Fallback to original fixed position approach
  }

  // Define regions relative to the barcode position
  let regions: Array<{name: string; x: number; y: number; w: number; h: number}>

  if (barcodeRegion) {
    // Extract tracking number directly below the detected barcode
    const barcodeBottom = barcodeRegion.y + barcodeRegion.height
    const textRegionHeight = height * 0.06 // 6% of image height for text

    regions = [
      // Right below barcode
      {name: 'Below barcode', x: 0.05, y: barcodeBottom, w: 0.9, h: textRegionHeight},
      // Slightly lower
      {name: 'Below barcode +5px', x: 0.05, y: barcodeBottom + 5, w: 0.9, h: textRegionHeight},
      // Slightly higher (in case barcode detection was slightly off)
      {
        name: 'Below barcode -5px',
        x: 0.05,
        y: Math.max(0, barcodeBottom - 5),
        w: 0.9,
        h: textRegionHeight,
      },
      // Full width
      {name: 'Below barcode (full width)', x: 0.0, y: barcodeBottom, w: 1.0, h: textRegionHeight},
      // Taller region
      {name: 'Below barcode (tall)', x: 0.05, y: barcodeBottom, w: 0.9, h: textRegionHeight * 1.5},
    ]
  } else {
    // Fallback: use fixed percentages from label top
    regions = [
      {
        name: 'Region 1 (81-87%)',
        x: 0.05,
        y: baseY + baseHeight * 0.81,
        w: 0.9,
        h: baseHeight * 0.06,
      },
      {
        name: 'Region 2 (80-86%)',
        x: 0.05,
        y: baseY + baseHeight * 0.8,
        w: 0.9,
        h: baseHeight * 0.06,
      },
      {
        name: 'Region 3 (82-88%)',
        x: 0.05,
        y: baseY + baseHeight * 0.82,
        w: 0.9,
        h: baseHeight * 0.06,
      },
      {
        name: 'Region 4 (79-85%)',
        x: 0.05,
        y: baseY + baseHeight * 0.79,
        w: 0.9,
        h: baseHeight * 0.06,
      },
      {
        name: 'Region 5 (83-89%)',
        x: 0.05,
        y: baseY + baseHeight * 0.83,
        w: 0.9,
        h: baseHeight * 0.06,
      },
    ]
  }

  const results: Array<{
    region: string
    barcode: string
    confidence: number
    length: number
    x: number
    y: number
    width: number
    height: number
  }> = []

  // Configure Tesseract for optimal digit recognition
  await worker.setParameters({
    tessedit_char_whitelist: '0123456789',
    tessedit_pageseg_mode: Tesseract.PSM.SINGLE_LINE,
    tessedit_ocr_engine_mode: Tesseract.OEM.LSTM_ONLY,
  })

  for (const region of regions) {
    const regionCanvas = document.createElement('canvas')
    const regionWidth =
      typeof region.w === 'number' && region.w <= 1 ? baseWidth * region.w : region.w
    const regionHeight = typeof region.h === 'number' ? region.h : height * 0.06
    regionCanvas.width = regionWidth
    regionCanvas.height = regionHeight

    const regionCtx = regionCanvas.getContext('2d')
    if (!regionCtx) continue

    // Calculate source coordinates
    const sourceX =
      baseX + (typeof region.x === 'number' && region.x <= 1 ? baseWidth * region.x : region.x)
    const sourceY = region.y

    regionCtx.drawImage(
      canvas,
      sourceX,
      sourceY,
      regionWidth,
      regionHeight,
      0,
      0,
      regionWidth,
      regionHeight
    )

    // Preprocess
    preprocessBarcodeImage(regionCanvas)

    try {
      const result = await worker.recognize(regionCanvas)
      const barcode = extractTrackingNumber(result.data.text)

      if (barcode && barcode.length >= 10) {
        // Normalize coordinates relative to label bounds, not full canvas
        results.push({
          region: region.name,
          barcode,
          confidence: result.data.confidence,
          length: barcode.length,
          x: (sourceX - baseX) / baseWidth, // Relative to label
          y: (sourceY - baseY) / baseHeight, // Relative to label
          width: regionWidth / baseWidth,
          height: regionHeight / baseHeight,
        })
      }
    } catch (error) {
      // Silent fail for individual regions
    }
  }

  if (results.length > 0) {
    const bestResult = results.reduce((best, current) => {
      const currentLengthScore = Math.abs(current.length - 21)
      const bestLengthScore = Math.abs(best.length - 21)

      if (currentLengthScore < bestLengthScore) return current
      if (currentLengthScore > bestLengthScore) return best

      return current.confidence > best.confidence ? current : best
    })

    return {
      success: true,
      barcode: bestResult.barcode,
      region: {
        x: bestResult.x,
        y: bestResult.y,
        width: bestResult.width,
        height: bestResult.height,
      },
    }
  }

  return {success: false}
}
